import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ColorSchemeService } from './services/color-scheme.service';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { Location } from '@angular/common';
import { filter } from 'rxjs/operators';
import { interval } from 'rxjs';
import { SwUpdate } from '@angular/service-worker';
import { UserDataService } from './services/core/user-data.service';
import { HeaderComponent } from './components/header/header.component';
import { TourStepTemplateComponent, TourAnchorMatMenuDirective } from 'ngx-ui-tour-md-menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
import { LoggingService } from './services/core/logging.service';

const ROUTES_WITHOUT_HEADER = [
  '/login',
  '/login-sso',
  '/interface',
  '/interface-p3',
  '/project',
  '/propagation',
  '/smart-label',
  '/mosaic',
  '/report',
  '/report-public',
  '/loading',
];

function isRouteWithoutHeader(path: string): boolean {
  return ROUTES_WITHOUT_HEADER.some((r) => path === r || path.startsWith(r + '/'));
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [HeaderComponent, RouterOutlet, TourStepTemplateComponent, TourAnchorMatMenuDirective],
})
export class AppComponent implements OnInit {
  translate = inject(TranslateService);
  private colorScheme = inject(ColorSchemeService);
  router = inject(Router);
  private location = inject(Location);
  user = inject(UserDataService);
  private swUpdate = inject(SwUpdate);
  private snackBar = inject(MatSnackBar);
  private titleService = inject(Title);
  private logging = inject(LoggingService, { optional: true });

  title = 'pantera-web';
  // Calculado já a partir da URL inicial (não só depois do primeiro NavigationEnd, que só
  // dispara após guard/lazy-load resolverem) — senão o header pisca por alguns segundos em
  // rotas sem header antes de sumir, o que não pode acontecer no /report/:idReport (link
  // compartilhável, o header do Pantera nunca pode aparecer nele). Location (em vez de
  // window.location direto) resolve sozinho hash vs path strategy e é mockável em teste.
  public showHeader = !isRouteWithoutHeader(this.location.path().split('?')[0]);

  ngOnInit() {
    if (!environment.production) {
      const currentTitle = this.titleService.getTitle();
      if (!currentTitle.startsWith('[DEV]')) {
        this.titleService.setTitle(`[DEV] ${currentTitle || 'Pantera'}`);
      }
    }
    this.translate.addLangs(['pt-br', 'en', 'es']);
    this.translate.setDefaultLang('pt-br');
    this.translate.use('pt-br');
    this.colorScheme.load();
    this.logging?.startSessionHealthMonitoring();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const path = event.urlAfterRedirects.split('?')[0];
        this.showHeader = !isRouteWithoutHeader(path);
      });
    if (this.swUpdate.isEnabled) {
      // Escutar continuamente por atualizações (sem take(1))
      this.swUpdate.versionUpdates.subscribe((event) => {
        if (event.type === 'VERSION_READY') {
          console.info('⚠️ Nova versão detectada:', event);
          this.showUpdateNotification();
        } else if (event.type === 'NO_NEW_VERSION_DETECTED') {
          console.info('✅ App is at latest version');
        }
      });

      // Verificar atualizações periodicamente (a cada 60 segundos)
      interval(60000).subscribe(() => {
        this.swUpdate.checkForUpdate().then((updateFound) => {
          if (updateFound) {
            console.info('✅ Verificação periódica encontrou atualização');
          }
        });
      });
    }
  }

  // TODO TRAZUDIR
  private showUpdateNotification(): void {
    const snackBarRef = this.snackBar.open('Nova versão disponível!', 'Atualizar Agora', {
      duration: 0, // Não fecha automaticamente
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['update-snackbar'],
    });

    snackBarRef.onAction().subscribe(() => {
      console.info('🔄 Usuário clicou para atualizar. Recarregando...');
      window.location.reload();
    });
  }
}
